/**
 * Setting the bank's account of events against our own.
 *
 * WHAT THIS SCREEN IS FOR
 * -----------------------
 * The books say the bank account holds one figure. The bank says another.
 * Explaining the whole of that difference is the job, and what it catches is
 * rarely arithmetic — it is a payment entered twice, a cheque nobody chased,
 * a monthly charge that has never been in the books, or money leaving an
 * account nobody authorised.
 *
 * HOW IT IS LAID OUT
 * ------------------
 * Two lists, and they are not equally urgent.
 *
 * What the bank has that the books do not is work: every line is an entry
 * still missing from the journal. That list is first, and it is the one with
 * buttons on it.
 *
 * What the books have that the bank has not yet shown is usually nothing to
 * worry about — a cheque takes days to clear. It is shown second, quieter,
 * because most of the time the right response is to leave it alone.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { parseStatement } from './statement.js';
import { AddBankAccount } from './AddBankAccount.jsx';

export default function ReconciliationPage() {
  const [accountId, setAccountId] = useState(null);
  const [asAt, setAsAt] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);

  const { data: accounts } = useQuery({
    queryKey: ['banking', 'accounts'],
    queryFn: () => api.get('/banking/accounts'),
  });

  const chosen = accountId ?? accounts?.accounts?.[0]?.id ?? null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['banking', 'reconciliation', chosen, asAt],
    queryFn: () =>
      api.get(withQuery(`/banking/accounts/${chosen}/reconciliation`, { as_at: asAt })),
    enabled: Boolean(chosen),
  });

  if (!accounts) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner label="Loading" />
      </div>
    );
  }

  // An empty state that can be acted on. It used to be a sentence and nothing
  // else, on a screen where nothing else in the application could fix it.
  if (accounts.accounts.length === 0) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-8">
        <p className="mb-5 text-center text-ink-2 text-sm">
          No bank account has been set up yet, so there is nothing to reconcile.
        </p>
        <AddBankAccount onDone={(account) => setAccountId(account.id)} />
      </div>
    );
  }

  if (addingAccount) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-8">
        <AddBankAccount
          onDone={(account) => {
            setAccountId(account.id);
            setAddingAccount(false);
          }}
          onCancel={() => setAddingAccount(false)}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Bank reconciliation</h1>
          <p className="mt-0.5 text-sm text-ink-2">
            What the bank says, set against what the books say.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          {accounts.accounts.length > 1 && (
            <label>
              <span className="mb-1.5 block text-xs text-ink-3">Account</span>
              <select
                value={chosen ?? ''}
                onChange={(e) => setAccountId(e.target.value)}
                className="h-11 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none"
              >
                {accounts.accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.bank_name} — {a.account_title}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label>
            <span className="mb-1.5 block text-xs text-ink-3">Up to</span>
            <input
              type="date"
              value={asAt}
              onChange={(e) => setAsAt(e.target.value)}
              className="h-11 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
          </label>

          <Button onClick={() => setShowImport((open) => !open)}>
            {showImport ? 'Close' : 'Import statement'}
          </Button>

          <Button variant="secondary" onClick={() => setAddingAccount(true)}>
            Add account
          </Button>
        </div>
      </div>

      {showImport && <ImportPanel accountId={chosen} onDone={() => setShowImport(false)} />}

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Working it out" />
        </div>
      ) : isError || !data ? (
        <p className="py-16 text-center text-sm text-ink-2">
          Could not work this out. Check the branch computer is running.
        </p>
      ) : (
        <>
          {/* Loud, because this means the arithmetic itself is wrong — not
              that there is work to do. Nothing on the page can be trusted. */}
          {!data.arithmeticSound && (
            <p className="mb-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
              The two sides do not add up, which should not be possible. Something is wrong with the
              books themselves rather than with this reconciliation.
            </p>
          )}

          {data.coverage.warning && (
            <p
              className={`mb-4 rounded-lg px-3.5 py-2.5 text-sm ${
                data.coverage.statementLines === 0
                  ? 'bg-surface-3 text-ink-2'
                  : 'bg-warn-bg text-warn'
              }`}
            >
              {data.coverage.warning}
            </p>
          )}

          <Summary data={data} />

          <Section
            title="On the statement, not in the books"
            note="Each of these is an entry the books are still missing — a charge, interest, or something nobody recorded."
            count={data.notInBooks.lines.length}
          >
            {data.notInBooks.lines.map((line) => (
              <StatementLine
                key={line.id}
                line={line}
                accountId={chosen}
                asAt={asAt}
                suggestion={data.suggestions.find((s) => s.statementLineId === line.id)}
                candidates={[
                  ...data.depositsInTransit.lines,
                  ...data.paymentsNotCleared.lines,
                ].filter((c) => Math.abs(c.amount - line.amount) < 0.005)}
              />
            ))}
          </Section>

          <Section
            title="In the books, not yet on the statement"
            note="Usually nothing to act on — a cheque takes days to clear. Worth a look if something here is old."
            count={data.depositsInTransit.lines.length + data.paymentsNotCleared.lines.length}
            quiet
          >
            <LedgerLines
              label="Money in, not yet shown"
              lines={data.depositsInTransit.lines}
              total={data.depositsInTransit.total}
            />
            <LedgerLines
              label="Money out, not yet taken"
              lines={data.paymentsNotCleared.lines}
              total={data.paymentsNotCleared.total}
            />
          </Section>
        </>
      )}
    </div>
  );
}

/** The two sides, each brought to the same figure. */
function Summary({ data }) {
  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <h3 className="text-sm font-medium text-ink-2">Per the bank statement</h3>
          <Row label="Balance" value={data.statementBalance} />
          <Row label="Add: money in, not yet shown" value={data.depositsInTransit.total} />
          <Row label="Add: money out, not yet taken" value={data.paymentsNotCleared.total} />
          <Row label="Adjusted" value={data.adjustedStatement} strong />
        </div>

        <div>
          <h3 className="text-sm font-medium text-ink-2">Per the books</h3>
          <Row label="Balance" value={data.booksBalance} />
          <Row label="Add: on the statement, not booked" value={data.notInBooks.total} />
          {/* Keeps the two columns' "Adjusted" lines on the same baseline, so
              the figures that must agree sit side by side. */}
          <Row label="" value={null} />
          <Row label="Adjusted" value={data.adjustedBooks} strong />
        </div>
      </div>

      <div
        className={`mt-4 flex items-center justify-between border-t-2 pt-3 ${
          data.reconciled ? 'border-brand-400' : 'border-rule'
        }`}
      >
        <span className="text-sm font-medium text-ink">
          {data.reconciled
            ? 'Reconciled — every line on the statement is accounted for'
            : data.coverage.statementLines === 0
              ? 'Nothing to reconcile against yet'
              : `${data.coverage.matched} of ${data.coverage.statementLines} statement lines accounted for`}
        </span>
        {data.arithmeticSound && (
          <span className="text-xs text-ink-3">
            Both sides agree at {formatPKR(data.adjustedBooks)}
          </span>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, strong = false }) {
  return (
    <div
      className={`flex items-baseline justify-between gap-3 py-0.5 ${
        strong ? 'mt-1.5 border-t border-rule pt-1.5' : ''
      }`}
    >
      <span className={`text-sm ${strong ? 'font-medium text-ink' : 'text-ink-2'}`}>
        {label || '\u00a0'}
      </span>
      <span
        className={`shrink-0 tabular-nums ${strong ? 'font-semibold text-ink' : 'text-sm text-ink'}`}
      >
        {value === null ? '' : formatPKR(value)}
      </span>
    </div>
  );
}

function Section({ title, note, count, quiet = false, children }) {
  return (
    <section
      className={`mb-5 rounded-xl border border-rule bg-surface p-5 ${quiet ? 'opacity-95' : ''}`}
    >
      <header className="mb-3">
        <h2 className="font-medium text-ink">
          {title} <span className="text-ink-3">({count})</span>
        </h2>
        <p className="mt-0.5 text-sm text-ink-3">{note}</p>
      </header>

      {count === 0 ? (
        <p className="py-2 text-sm text-ink-3">Nothing here.</p>
      ) : (
        <div className="space-y-1">{children}</div>
      )}
    </section>
  );
}

/**
 * One line the bank has that the books do not.
 *
 * A proposed match is shown as a proposal — named, with its date — so the
 * person can see what they are agreeing to. A match applied without being
 * read is a guess with a signature on it.
 */
function StatementLine({ line, accountId, asAt, suggestion, candidates }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const match = useMutation({
    mutationFn: (journalLineId) =>
      api.post(`/banking/statement-lines/${line.id}/match`, { journalLineId }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['banking', 'reconciliation', accountId, asAt] }),
  });

  const proposed = suggestion ? candidates.find((c) => c.id === suggestion.journalLineId) : null;

  return (
    <div className="rounded-lg border border-rule px-3 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <span className="mr-2 text-xs tabular-nums text-ink-3">{line.txn_date}</span>
          <span className="text-sm text-ink">{line.description}</span>
          {line.reference && <span className="ml-2 text-xs text-ink-3">{line.reference}</span>}
        </div>

        <div className="flex items-center gap-3">
          <span className={`text-sm tabular-nums ${line.amount < 0 ? 'text-ink-2' : 'text-ink'}`}>
            {line.amount < 0 ? `(${formatPKR(Math.abs(line.amount))})` : formatPKR(line.amount)}
          </span>

          {candidates.length > 0 && (
            <Button variant="secondary" onClick={() => setOpen((was) => !was)}>
              {open ? 'Cancel' : 'Match'}
            </Button>
          )}
        </div>
      </div>

      {proposed && !open && (
        <p className="mt-1 text-xs text-ink-3">
          Looks like {proposed.description} of {proposed.entry_date}
        </p>
      )}

      {open && (
        <div className="mt-2 space-y-1 border-t border-rule pt-2">
          {candidates.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              onClick={() => match.mutate(candidate.id)}
              disabled={match.isPending}
              className="flex w-full items-baseline justify-between gap-3 rounded px-2 py-1.5 text-left hover:bg-surface-3 disabled:opacity-50"
            >
              <span className="min-w-0 text-sm text-ink-2">
                <span className="mr-2 text-xs tabular-nums text-ink-3">{candidate.entry_date}</span>
                {candidate.description}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-ink-3">
                {candidate.entry_number}
              </span>
            </button>
          ))}

          {/* Said plainly, because the alternative reading is that the system
              has nothing to offer rather than that it will not guess. */}
          {candidates.length > 1 && (
            <p className="px-2 pt-1 text-xs text-ink-3">
              More than one entry fits this amount, so nothing is proposed. Pick the right one.
            </p>
          )}
        </div>
      )}

      {match.isError && (
        <p className="mt-1 text-xs text-bad">{match.error?.message ?? 'That did not work.'}</p>
      )}
    </div>
  );
}

function LedgerLines({ label, lines, total }) {
  if (lines.length === 0) return null;

  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between">
        <h4 className="text-sm font-medium text-ink-2">
          {label} <span className="text-ink-3">({lines.length})</span>
        </h4>
        <span className="text-sm tabular-nums text-ink">{formatPKR(total)}</span>
      </div>

      <div className="mt-1">
        {/* Only the oldest few. A branch taking card payments all day has
            hundreds of these and listing them all buries the one that is
            three weeks old and should have cleared. */}
        {lines.slice(-5).map((line) => (
          <div key={line.id} className="flex items-baseline justify-between gap-3 py-0.5">
            <span className="min-w-0 truncate text-sm text-ink-3">
              <span className="mr-2 text-xs tabular-nums">{line.entry_date}</span>
              {line.description}
            </span>
            <span className="shrink-0 text-sm tabular-nums text-ink-3">
              {formatPKR(Math.abs(line.amount))}
            </span>
          </div>
        ))}

        {lines.length > 5 && (
          <p className="pt-1 text-xs text-ink-3">and {lines.length - 5} more, not shown</p>
        )}
      </div>
    </div>
  );
}

/** Paste a statement in. */
function ImportPanel({ accountId, onDone }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');

  const parsed = parseStatement(text);

  const send = useMutation({
    mutationFn: () =>
      api.post(`/banking/accounts/${accountId}/statement-lines`, {
        lines: parsed.lines,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banking', 'reconciliation'] });
      setText('');
      onDone();
    },
  });

  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <h2 className="font-medium text-ink">Import a statement</h2>
      <p className="mt-0.5 text-sm text-ink-3">
        Paste the rows from the bank&rsquo;s file or from a spreadsheet. A date, what it was, and
        the amount — with a heading row or without.
      </p>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={7}
        spellCheck={false}
        placeholder={
          'Date,Description,Amount\n25/08/2026,Monthly service charge,-1500\n25/08/2026,Cash deposit,80000'
        }
        className="mt-3 w-full rounded-lg border border-rule bg-surface-3 p-3 font-mono text-xs text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
      />

      {text.trim() && (
        <div className="mt-2 text-sm">
          <p className="text-ink-2">
            {parsed.lines.length} line{parsed.lines.length === 1 ? '' : 's'} read
            {parsed.skipped > 0 && `, ${parsed.skipped} could not be`}
          </p>

          {/* Named rather than dropped: a statement quietly short by three
              rows still reconciles to something, and that something is wrong. */}
          {parsed.problems.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-xs text-warn">
              {parsed.problems.slice(0, 6).map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
              {parsed.problems.length > 6 && (
                <li className="text-ink-3">and {parsed.problems.length - 6} more</li>
              )}
            </ul>
          )}
        </div>
      )}

      {send.isError && (
        <p className="mt-2 text-sm text-bad">{send.error?.message ?? 'That did not work.'}</p>
      )}

      {send.data && (
        <p className="mt-2 text-sm text-ink-2">
          {send.data.imported} added
          {send.data.duplicates > 0 && `, ${send.data.duplicates} were already there`}
        </p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button
          onClick={() => send.mutate()}
          disabled={parsed.lines.length === 0 || send.isPending}
        >
          {send.isPending
            ? 'Adding'
            : `Add ${parsed.lines.length} line${parsed.lines.length === 1 ? '' : 's'}`}
        </Button>
      </div>
    </div>
  );
}
